import React, { useState, useRef, useEffect } from 'react';
import { synthesizeSpeech, TTSVoice } from '../../services/api';
import Button from './Button';

interface AudioPlayerProps {
  text: string;
  voice?: TTSVoice;
  maxPlays?: number;
  onPlay?: () => void;
  onComplete?: () => void;
  size?: 'small' | 'medium' | 'large';
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({
  text,
  voice = 'en-US-JennyNeural',
  maxPlays = 3,
  onPlay,
  onComplete,
  size = 'large',
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [playCount, setPlayCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);

  // Reset play count and audio when text changes (new exercise)
  useEffect(() => {
    setPlayCount(0);
    setError(null);
    setIsPlaying(false);
    setIsLoading(false);
    // Clear cached audio URL for the new text
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
  }, [text]);

  const canPlay = playCount < maxPlays;

  const handlePlay = async () => {
    if (!canPlay || isPlaying || isLoading) return;

    setError(null);
    setIsLoading(true);

    try {
      // Fetch audio if not already fetched
      if (!audioUrlRef.current) {
        const audioBlob = await synthesizeSpeech(text, voice);
        audioUrlRef.current = URL.createObjectURL(audioBlob);
      }

      // Create and play audio
      const audio = new Audio(audioUrlRef.current);
      audioRef.current = audio;

      audio.onplay = () => {
        setIsPlaying(true);
        setIsLoading(false);
        onPlay?.();
      };

      audio.onended = () => {
        setIsPlaying(false);
        setPlayCount((prev) => prev + 1);
        onComplete?.();
      };

      audio.onerror = () => {
        setError('Could not play audio');
        setIsPlaying(false);
        setIsLoading(false);
      };

      await audio.play();
    } catch (err) {
      console.error('Audio playback error:', err);
      setError('Could not play audio');
      setIsLoading(false);
    }
  };

  const getButtonContent = () => {
    if (isLoading) {
      return (
        <>
          <span className="animate-spin">...</span>
          Loading
        </>
      );
    }

    if (isPlaying) {
      return (
        <>
          <span className="animate-pulse">...</span>
          Playing
        </>
      );
    }

    const remaining = maxPlays - playCount;
    return (
      <>
        <span className="text-2xl">{'>'}</span>
        Listen ({remaining} left)
      </>
    );
  };

  return (
    <div className="flex flex-col items-center gap-2">
      <Button
        onClick={handlePlay}
        disabled={!canPlay || isPlaying || isLoading}
        variant={isPlaying ? 'success' : 'primary'}
        size={size}
      >
        {getButtonContent()}
      </Button>

      {error && <p className="text-kids-error text-sm">{error}</p>}

      {!canPlay && (
        <p className="text-gray-500 dark:text-gray-400 text-sm">No more plays left. Type your answer!</p>
      )}
    </div>
  );
};

export default AudioPlayer;
